/**
 * Production-ready reimagined Contribution History component.
 *
 * Renders inside the existing `slr.dashboard` route when the
 * `reimaginedAccountActivity` feature flag is enabled.
 *
 * Strips out: demo mode, DataIndicator badges, "Back to Finance" link,
 * links to reimagined-only routes (optimizer, etc).
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { eq, or } from 'tio-ui/utilities';
import { VStack } from 'tio-ui/components/layout';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import ReSelect from 'tio-reimagined/components/select';
import Checkbox from 'tio-reimagined/components/checkbox';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { Heading, Text } from 'tio-ui/components/typography';
import { formatCurrency } from 'tio-reimagined/format';
import ContributionHero from 'tio-employee/components/reimagined/contribution-hero';
import AccountSelectorDrawer from 'tio-employee/components/reimagined/account-selector-drawer';
import PdfExportDrawer from 'tio-employee/components/reimagined/pdf-export-drawer';
import BenefitDetailDrawer from 'tio-employee/components/reimagined/benefit-detail-drawer';
import type { ContributionHistoryData } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { EmployerContributions } from 'tio-employee/services/career-pathways-api';
import type SessionContextService from 'tio-employee/services/session-context';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type IntlService from 'ember-intl/services/intl';
import type AccountModel from 'tio-common/models/account';
import type PersonModel from 'tio-common/models/person';
import type PostalAddressModel from 'tio-common/models/postal-address';
import type RouterService from '@ember/routing/router-service';
import type StoreService from 'ember-data/store';
import {
  PhBank,
  PhCaretUp,
  PhCaretDown,
  PhPencilSimple,
  PhMoneyWavy,
  PhDownloadSimple,
  PhCloudArrowUp,
} from 'ember-phosphor-icons';

// Server may include computed contribution fields not declared on AccountModel
type AccountWithContributions = AccountModel & {
  totalContributionAmount?: number;
  contributionCount?: number;
};

export interface ReimaginedContributionHistoryModel {
  contributionHistory: ContributionHistoryData;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  person: PersonModel;
}

interface ReimaginedContributionHistorySignature {
  Args: {
    model: ReimaginedContributionHistoryModel;
  };
}

export default class ReimaginedContributionHistory extends Component<ReimaginedContributionHistorySignature> {
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare store: StoreService;
  @service declare reimaginedData: ReimaginedDataService;

  @tracked isAccountDrawerOpen = false;
  @tracked isPdfDrawerOpen = false;
  @tracked isBenefitDrawerOpen = false;
  @tracked showEditAddress = false;
  @tracked editAddressHasSubmitted = false;
  @tracked selectedAddress?: PostalAddressModel;
  @tracked addressConfirmationChecked = false;
  @tracked expandedAccounts = new Set<string>();

  get history() {
    return this.args.model.contributionHistory;
  }

  get contributions() {
    return this.args.model.employerContributions.contributions;
  }

  get employerName() {
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get person() {
    return this.args.model.person;
  }

  get activeAccounts() {
    return this.person.activeAccounts;
  }

  get accountReceivingContributions() {
    return this.person.accountReceivingContributions;
  }

  get employeeId() {
    return this.sessionContext.currentEmployee?.id ?? '';
  }

  get isAddressSubmitDisabled() {
    return (
      !this.selectedAddress || this.editAddressHasSubmitted || !this.addressConfirmationChecked
    );
  }

  get selectedAddressId() {
    return this.selectedAddress?.id || '';
  }

  /**
   * User's first name for greeting
   */
  get firstName() {
    return this.sessionContext.user?.person?.firstName || 'there';
  }

  /**
   * Time-based greeting
   */
  get greeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }

  /**
   * Projected annual contribution
   */
  get projectedAnnual() {
    return this.contributions.projectedAnnual || 0;
  }

  /**
   * Benefit year to date
   */
  get benefitYearToDate() {
    return this.contributions.yearToDate || 0;
  }

  /**
   * Monthly contribution amount
   */
  get monthlyContribution() {
    return Math.round(this.projectedAnnual / 12) || 0;
  }

  /**
   * Lifetime employer benefit
   */
  get lifetimeBenefit() {
    return this.contributions.lifetime || 0;
  }

  /**
   * Estimated remaining benefit for the year
   */
  get estimatedRemaining() {
    return Math.max(this.projectedAnnual - this.benefitYearToDate, 0);
  }

  /**
   * Benefit year range (start and end)
   */
  get benefitYearRange() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const startYear = month >= 9 ? year : year - 1;
    const endYear = startYear + 1;
    return `10/01/${String(startYear).slice(2)}-10/01/${String(endYear).slice(2)}`;
  }

  /**
   * Benefit year progress percentage
   */
  get benefitYearProgressPercent() {
    if (this.projectedAnnual === 0) return 0;
    return Math.round((this.benefitYearToDate / this.projectedAnnual) * 100);
  }

  /**
   * Hero variant based on available data:
   * 1 = tracker + 2 cards (new user, no meaningful lifetime)
   * 2 = tracker + 3 cards (full data with lifetime)
   * 3 = no tracker, 4 equal cards (no benefit year tracking)
   */
  get heroVariant(): 1 | 2 | 3 {
    if (!this.projectedAnnual) return 3;
    if (this.lifetimeBenefit > this.benefitYearToDate) return 2;
    return 1;
  }

  /**
   * Formatted last payment date (e.g. "12/2025")
   */
  get lastPaymentFormatted() {
    const payment = this.history.lastPayment;
    if (!payment?.paidDate) return '';
    const date = new Date(payment.paidDate);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  }

  get lastPaymentAmount() {
    return this.history.lastPayment?.amount ?? 0;
  }

  /**
   * Next expected contribution date
   */
  get nextContributionDate() {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      year: 'numeric',
    }).format(nextMonth);
  }

  get addressOptions() {
    return (
      // @ts-expect-error
      this.addressData.value?.map((address: PostalAddressModel) => ({
        label: htmlSafe(
          address.addressLine1 +
            (address.addressLine2 ? '\n ' + address.addressLine2 : '') +
            (address.addressLine3 ? '\n ' + address.addressLine3 : '')
        ),
        key: address.id,
        address,
      })) || []
    );
  }

  addressData = trackedFunction(this, async () => {
    // @ts-expect-error
    return await this.store.query('postal-address', {
      filter: {
        financialInstitution: this.person.accountReceivingContributions?.financialInstitution?.id,
        isActive: true,
        type: 'PAYMENT',
      },
      sort: 'address-line1',
      page: {
        offset: 0,
        limit: 400,
      },
    });
  });

  toggleEditAddress = () => {
    this.showEditAddress = !this.showEditAddress;
    this.selectedAddress = undefined;
    this.addressConfirmationChecked = false;
    this.editAddressHasSubmitted = false;
  };

  setSelectedAddress = (id: string | null) => {
    this.selectedAddress = this.addressOptions.find((a) => a.key === id)?.address;
  };

  updateAddress = () => {
    return this.updateAddressTask.perform();
  };

  updateAddressTask = dropTask(async () => {
    this.editAddressHasSubmitted = true;
    const account = this.person.accountReceivingContributions;
    if (!this.selectedAddress || !account) {
      this.editAddressHasSubmitted = false;
      return;
    }
    try {
      const newAddress = this.addressData.value?.find(
        // @ts-expect-error
        (a: PostalAddressModel) => a.id === this.selectedAddress?.id
      );
      if (!newAddress) {
        throw new Error('Address not found');
      }
      account.postalAddress = newAddress as PostalAddressModel;
      await account.save();
      this.toggleEditAddress();
      this.router.refresh('authenticated.slr.dashboard');
      const successMessage = this.intl.t('common.accounts.update_address.success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    } catch (e) {
      console.error(e);
      const errorMessage = this.intl.t('common.accounts.update_address.error');
      this.notifications.add(errorMessage, {
        appearance: 'error',
      });
    } finally {
      this.editAddressHasSubmitted = false;
    }
  });

  toggleAccountDrawer = () => {
    this.isAccountDrawerOpen = !this.isAccountDrawerOpen;
  };

  togglePdfDrawer = () => {
    this.isPdfDrawerOpen = !this.isPdfDrawerOpen;
  };

  toggleBenefitDrawer = () => {
    this.isBenefitDrawerOpen = !this.isBenefitDrawerOpen;
  };

  get plan() {
    return this.sessionContext.currentEmployee?.plan ?? null;
  }

  onAccountChanged = () => {
    this.router.refresh('authenticated.slr.dashboard');
  };

  get receivingLoans() {
    return this.activeAccounts.filter(
      (account) => account.id === this.accountReceivingContributions?.id
    );
  }

  get otherLoans() {
    return this.activeAccounts.filter(
      (account) => account.id !== this.accountReceivingContributions?.id
    );
  }

  toggleAccountExpansion = (accountId: string) => {
    if (this.expandedAccounts.has(accountId)) {
      this.expandedAccounts.delete(accountId);
    } else {
      this.expandedAccounts.add(accountId);
    }
    this.expandedAccounts = new Set(this.expandedAccounts);
  };

  isAccountExpanded = (accountId: string) => {
    return this.expandedAccounts.has(accountId);
  };

  getAccountContributionAmount = (account: AccountWithContributions) => {
    return account.totalContributionAmount ?? 0;
  };

  getAccountContributionCount = (account: AccountWithContributions) => {
    return account.contributionCount ?? 0;
  };

  getAccountBalance = (account: AccountModel) => {
    return account.loanTotal;
  };

  getAccountLoanCount = (account: AccountModel) => {
    return account.summary?.loanCount ?? 0;
  };

  get recentPayments() {
    return this.history.payments.filter((payment) => !payment.isFuture).slice(0, 3);
  }

  get lastPaymentIndex() {
    return this.recentPayments.length - 1;
  }

  getPaymentStatusLabel = (status: 'sent' | 'in_progress' | 'pending') => {
    if (status === 'sent') return 'Sent';
    if (status === 'in_progress') return 'In Progress';
    return 'Pending';
  };

  getPaymentStatusIntent = (status: 'sent' | 'in_progress' | 'pending') => {
    if (status === 'sent') return 'success';
    if (status === 'in_progress') return 'primary';
    return 'default';
  };

  // Action Items - provided by service (demo or live)
  get actionItems() {
    return this.history.actionItems;
  }

  @tracked expandedActionItems = new Set<string>();

  toggleActionItem = (itemId: string) => {
    if (this.expandedActionItems.has(itemId)) {
      this.expandedActionItems.delete(itemId);
    } else {
      this.expandedActionItems.add(itemId);
    }
    this.expandedActionItems = new Set(this.expandedActionItems);
  };

  isActionItemExpanded = (itemId: string) => {
    return this.expandedActionItems.has(itemId);
  };

  // Payment History by Year - provided by service (demo or live)
  get paymentsByYear() {
    return this.history.paymentsByYear;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Hero Banner - Student Loan Contributions }}
      <ContributionHero
        @greeting={{this.greeting}}
        @firstName={{this.firstName}}
        @variant={{this.heroVariant}}
        @estimatedRemaining={{this.estimatedRemaining}}
        @benefitYearProgressPercent={{this.benefitYearProgressPercent}}
        @benefitYearRange={{this.benefitYearRange}}
        @projectedAnnual={{this.projectedAnnual}}
        @employerContribution={{this.benefitYearToDate}}
        @lifetimeBenefit={{this.lifetimeBenefit}}
        @nextContribution={{this.monthlyContribution}}
        @nextContributionDate={{this.nextContributionDate}}
        @lastPaymentAmount={{this.lastPaymentAmount}}
        @lastPaymentFormatted={{this.lastPaymentFormatted}}
        @onViewBenefit={{this.toggleBenefitDrawer}}
      />

      {{! Action Items Section }}
      <div class="mt-6 bg-gray-50 rounded-2xl p-4">
        <div class="flex items-center justify-between px-1 mb-4">
          <div class="flex items-center gap-2">
            <Heading @level={{3}} class="text-[18px] font-semibold text-gray-800 leading-none">
              Action Items
            </Heading>
            <Text class="text-base font-medium text-gray-700">
              ({{this.actionItems.length}})
            </Text>
          </div>
        </div>

        <div class="flex flex-col gap-3">
          {{#each this.actionItems as |item|}}
            <div class="bg-white border border-gray-300 rounded-2xl p-4">
              <div class="flex items-center justify-between">
                <div class="flex flex-col gap-1.5">
                  <div class="flex items-center gap-2">
                    <Heading @level={{4}} class="text-base font-semibold text-gray-900">
                      {{item.title}}
                    </Heading>
                    <Chip
                      @intent={{item.badgeIntent}}
                      @size="sm"
                      class="text-xs px-2 py-1 h-4 leading-tight"
                    >
                      {{item.badge}}
                    </Chip>
                  </div>
                  <Text class="text-gray-500 text-sm font-medium">
                    {{item.description}}
                  </Text>
                </div>
                <button
                  type="button"
                  {{on "click" (fn this.toggleActionItem item.id)}}
                  class="w-6 h-6"
                  aria-label={{if
                    (this.isActionItemExpanded item.id)
                    "Collapse action item"
                    "Expand action item"
                  }}
                >
                  {{#if (this.isActionItemExpanded item.id)}}
                    <PhCaretUp @size={{24}} class="text-gray-600" />
                  {{else}}
                    <PhCaretDown @size={{24}} class="text-gray-600" />
                  {{/if}}
                </button>
              </div>

              {{! Expandable Upload Section }}
              {{#if (this.isActionItemExpanded item.id)}}
                <div class="mt-6 bg-blue-50 rounded-xl px-2 py-6">
                  <div class="px-7 flex flex-col gap-6">
                    {{! Upload Box }}
                    <div
                      class="bg-gray-200/30 border-2 border-dashed border-gray-400 rounded-2xl h-60 flex flex-col items-center justify-center gap-4 px-8"
                    >
                      {{! Icon + Text }}
                      <div class="flex flex-col items-center gap-2">
                        <PhCloudArrowUp @size={{40}} class="text-gray-700" />
                        <Text class="text-base font-medium text-gray-800 text-center">
                          <span class="font-semibold text-gray-900 underline">Select a File</span>
                          or drop here to upload
                        </Text>
                      </div>

                      {{! Small Text }}
                      <div
                        class="flex flex-col items-center gap-2 text-xs font-medium text-gray-700"
                      >
                        <Text class="text-xs">Supported Formats: PDF, WORD, DOC, JPEG</Text>
                        <Text class="text-xs">Max Size: 5MB</Text>
                      </div>
                    </div>

                    {{! Done Button (disabled state) }}
                    <Button
                      @intent="primary"
                      @appearance="default"
                      @size="md"
                      @label="Done"
                      @isDisabled={{true}}
                      class="w-50 opacity-40"
                    />
                  </div>
                </div>
              {{/if}}
            </div>
          {{/each}}
        </div>
      </div>

      {{! Loan Accounts Section }}
      <div class="mt-10">
        {{! Top-level header with Edit and Update buttons }}
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center gap-2">
            <PhBank @size={{20}} class="text-gray-700" />
            <Heading @level={{2}} class="text-[18px] font-semibold text-gray-900 leading-none">
              Loan Accounts
            </Heading>
          </div>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="flex items-center justify-center border-2 border-teal-600 rounded p-2 shrink-0 aspect-square"
              aria-label="Edit loan accounts"
            >
              <PhPencilSimple @size={{18}} class="text-teal-600" />
            </button>
            <Button
              @intent="default"
              @appearance="outlined"
              @size="md"
              @label="Update"
              class="border-2 border-teal-500 text-teal-600 hover:bg-teal-50 font-medium"
            />
          </div>
        </div>

        {{! Receiving Contributions Section }}
        {{#if this.receivingLoans.length}}
          <div class="bg-white border border-gray-300 rounded-3xl mb-4">
            <div class="p-6">
              <Heading @level={{3}} class="text-lg font-semibold text-gray-800 mb-4">
                Receiving Contributions
              </Heading>

              <div class="flex flex-col gap-3">
                {{#each this.receivingLoans as |account|}}
                  <div
                    class="bg-gray-50 border border-gray-300 rounded-2xl p-4 flex flex-col gap-2"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex flex-col gap-1.5">
                        <div class="flex items-center gap-2">
                          <Heading @level={{4}} class="text-base font-semibold text-gray-900">
                            {{account.financialInstitution.name}}
                          </Heading>
                          <Chip @intent="success" @size="sm">
                            Active
                          </Chip>
                        </div>
                        <div class="flex items-center gap-1.5">
                          <Text class="text-gray-500 text-sm font-medium">
                            {{formatCurrency (this.getAccountContributionAmount account)}}
                            received
                          </Text>
                          <div class="w-1 h-1 rounded-full bg-gray-400"></div>
                          <Text class="text-gray-500 text-sm font-medium">
                            {{this.getAccountContributionCount account}}
                            {{#if
                              (eq (this.getAccountContributionCount account) 1)
                            }}contribution{{else}}contributions{{/if}}
                          </Text>
                        </div>
                      </div>
                      <button
                        type="button"
                        {{on "click" (fn this.toggleAccountExpansion (or account.id ""))}}
                        class="w-6 h-6"
                        aria-label={{if
                          (this.isAccountExpanded (or account.id ""))
                          "Collapse account details"
                          "Expand account details"
                        }}
                        aria-expanded={{if
                          (this.isAccountExpanded (or account.id ""))
                          "true"
                          "false"
                        }}
                      >
                        {{#if (this.isAccountExpanded (or account.id ""))}}
                          <PhCaretUp @size={{24}} class="text-gray-600" />
                        {{else}}
                          <PhCaretDown @size={{24}} class="text-gray-600" />
                        {{/if}}
                      </button>
                    </div>

                    {{#if (this.isAccountExpanded (or account.id ""))}}
                      <div class="mt-4 pt-4 border-t border-gray-200">
                        <div class="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <Text class="text-gray-500">Balance</Text>
                            <Text class="font-semibold text-gray-900">
                              {{formatCurrency (this.getAccountBalance account)}}
                            </Text>
                          </div>
                          <div>
                            <Text class="text-gray-500">Loan Count</Text>
                            <Text class="font-semibold text-gray-900">
                              {{this.getAccountLoanCount account}}
                            </Text>
                          </div>
                        </div>
                      </div>
                    {{/if}}
                  </div>
                {{/each}}
              </div>
            </div>
          </div>
        {{/if}}

        {{! Other Linked Loans Section }}
        {{#if this.otherLoans.length}}
          <div class="bg-white border border-gray-300 rounded-3xl">
            <div class="p-6">
              <Heading @level={{3}} class="text-lg font-semibold text-gray-800 mb-4">
                Other Linked Loans
              </Heading>

              <div class="flex flex-col gap-3">
                {{#each this.otherLoans as |account|}}
                  <div
                    class="bg-gray-50 border border-gray-300 rounded-2xl p-4 flex items-center justify-between"
                  >
                    <div class="flex flex-col gap-1.5">
                      <div class="flex items-center gap-2">
                        <Heading @level={{4}} class="text-base font-semibold text-gray-900">
                          {{account.financialInstitution.name}}
                        </Heading>
                        <Chip @intent="default" @size="sm">
                          Inactive
                        </Chip>
                      </div>
                      <div class="flex items-center gap-1.5">
                        <Text class="text-gray-500 text-sm font-medium">
                          {{formatCurrency (this.getAccountContributionAmount account)}}
                          received
                        </Text>
                        <div class="w-1 h-1 rounded-full bg-gray-400"></div>
                        <Text class="text-gray-500 text-sm font-medium">No contributions</Text>
                      </div>
                    </div>
                    <button
                      type="button"
                      {{on "click" (fn this.toggleAccountExpansion (or account.id ""))}}
                      class="w-6 h-6"
                      aria-label={{if
                        (this.isAccountExpanded (or account.id ""))
                        "Collapse account details"
                        "Expand account details"
                      }}
                      aria-expanded={{if
                        (this.isAccountExpanded (or account.id ""))
                        "true"
                        "false"
                      }}
                    >
                      {{#if (this.isAccountExpanded (or account.id ""))}}
                        <PhCaretUp @size={{24}} class="text-gray-600" />
                      {{else}}
                        <PhCaretDown @size={{24}} class="text-gray-600" />
                      {{/if}}
                    </button>
                  </div>
                {{/each}}
              </div>
            </div>
          </div>
        {{/if}}

        {{! Empty state when no loan accounts }}
        {{#unless (or this.receivingLoans.length this.otherLoans.length)}}
          <div class="bg-white border border-gray-300 rounded-3xl">
            <div class="p-6">
              <div class="py-10 flex flex-col items-center justify-center gap-3 text-center">
                <Text class="text-sm text-gray-500">
                  No loan accounts connected yet.
                </Text>
              </div>
            </div>
          </div>
        {{/unless}}
      </div>

      {{! Payment History Section }}
      <div class="mt-10">
        {{! Section Header }}
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <PhMoneyWavy @size={{20}} class="text-gray-700" />
            <Heading @level={{2}} class="text-[18px] font-semibold text-gray-800 leading-none">
              Payment History
            </Heading>
          </div>
          <button
            type="button"
            {{on "click" this.togglePdfDrawer}}
            class="flex items-center justify-center border-2 border-teal-600 rounded-md p-2"
            aria-label="Download PDF"
          >
            <PhDownloadSimple @size={{18}} class="text-teal-600" />
          </button>
        </div>

        {{! Payment Cards Section }}
        {{#if this.paymentsByYear.length}}
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {{#each this.paymentsByYear as |yearData|}}
              <div
                class="bg-white border border-gray-300 rounded-2xl p-4 shadow-sm flex flex-col gap-8"
              >
                <div class="flex flex-col gap-4">
                  <Text class="text-gray-500 text-base font-semibold">
                    {{yearData.year}}
                    Contribution
                  </Text>
                  <Heading @level={{3}} class="text-gray-900 text-3xl font-semibold leading-none">
                    {{formatCurrency yearData.total}}
                  </Heading>
                </div>

                <div class="flex flex-col gap-4">
                  <div class="flex items-center gap-2">
                    <Text class="text-gray-500 text-sm font-medium">Account{{#if
                        (eq yearData.accounts.length 1)
                      }}{{else}}s{{/if}}:</Text>
                    <div class="flex items-center gap-1.5 flex-wrap">
                      {{#each yearData.accounts as |account index|}}
                        {{#if (eq index 0)}}
                          <div class="flex items-center gap-1.5">
                            <PhBank @size={{16}} class="text-gray-700" />
                            <Text class="text-gray-700 text-sm font-semibold">
                              {{account.name}}
                            </Text>
                          </div>
                        {{else}}
                          <Text class="text-gray-700 text-sm font-semibold">,</Text>
                          <div class="flex items-center gap-1.5">
                            <PhBank @size={{16}} class="text-gray-700" />
                            <Text class="text-gray-700 text-sm font-semibold">
                              {{account.name}}
                            </Text>
                          </div>
                        {{/if}}
                      {{/each}}
                    </div>
                  </div>

                  <div
                    class="flex items-center justify-between pt-2 border-t border-gray-200 text-xs"
                  >
                    <div class="flex items-center gap-1">
                      <PhMoneyWavy @size={{14}} class="text-teal-600" />
                      <Text class="text-gray-900 font-medium">
                        {{yearData.paymentCount}}
                        Payments
                      </Text>
                    </div>

                  </div>
                </div>
              </div>
            {{/each}}
          </div>
        {{else}}
          <div class="py-10 flex flex-col items-center justify-center gap-2 text-center">
            <Text class="text-sm text-gray-500">No payment history yet.</Text>
          </div>
        {{/if}}
      </div>

      {{! Drawers }}
      <AccountSelectorDrawer
        @isOpen={{this.isAccountDrawerOpen}}
        @onClose={{this.toggleAccountDrawer}}
        @accounts={{this.activeAccounts}}
        @currentAccount={{this.accountReceivingContributions}}
        @personId={{this.person.id}}
        @onAccountChanged={{this.onAccountChanged}}
      />

      <BenefitDetailDrawer
        @isOpen={{this.isBenefitDrawerOpen}}
        @onClose={{this.toggleBenefitDrawer}}
        @plan={{this.plan}}
      />

      <PdfExportDrawer
        @isOpen={{this.isPdfDrawerOpen}}
        @onClose={{this.togglePdfDrawer}}
        @employeeId={{this.employeeId}}
      />

      {{! Address Selection Modal }}
      <Modal @isOpen={{this.showEditAddress}} @onClose={{this.toggleEditAddress}} @size="xl">
        <:header>{{t "common.accounts.select_payment_address"}}</:header>
        <:body>
          <VStack class="p-0!">
            <p>{{t "slr.select_address_body_1"}}</p>
            <p class="italic text-gray-500">{{t "slr.select_address_body_2"}}</p>
            <form class="flex flex-col gap-y-4">
              <ReSelect
                @searchable={{true}}
                @clearable={{true}}
                @items={{this.addressOptions}}
                @selected={{this.selectedAddressId}}
                @onChange={{this.setSelectedAddress}}
                @label="Servicer Address"
              />
              <Checkbox
                @isDisabled={{this.updateAddressTask.isRunning}}
                @label="I confirm the selected payment address is correct and understand that incorrect addresses may cause payment to be sent to the wrong address and delays of 90 days or longer."
                @checked={{this.addressConfirmationChecked}}
                @onChange={{fn (mut this.addressConfirmationChecked)}}
              />
            </form>
          </VStack>
        </:body>
        <:footer>
          <Footer
            @isRunning={{this.updateAddressTask.isRunning}}
            @onCancel={{this.toggleEditAddress}}
            @onSubmit={{this.updateAddress}}
            @submitDisabled={{this.isAddressSubmitDisabled}}
          />
        </:footer>
      </Modal>
    </VStack>
  </template>
}
